// src/pages/Dashboard.js
import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import Sidebar from "../component/Sidebar";
import ChangePassword from "./ChangePassword"; 
import { X } from "lucide-react";

// Icons
import {
  Users,
  Building2,
  Briefcase,
  Calendar,
  CalendarDays,
  Clock,
  FileText,
  History,
  MapPin,
  Wallet,
  Activity,
} from "lucide-react";

import eyeLogo from "../assets/EyeLogo.png";
import hospitalBg from "../assets/hospital.jpeg";

const Dashboard = () => {
  const [counts, setCounts] = useState({});
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [admin, setAdmin] = useState(JSON.parse(localStorage.getItem("adminData")) || {});
  const [userPermissions, setUserPermissions] = useState(() => {
    const storedPerms = JSON.parse(localStorage.getItem("userPermissions") || "[]") || [];
    const fallbackPerms = (JSON.parse(localStorage.getItem("adminData") || "{}")?.permissions) || [];
    return storedPerms.length ? storedPerms : fallbackPerms;
  });
  const [activities, setActivities] = useState([]);
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);
  const dropdownRef = useRef();
  const navigate = useNavigate();
  const hasAllAccess = userPermissions.includes("ALL") || userPermissions.includes("all");

  // Fetch counts
  useEffect(() => {
    axios
      .get("/api/dashboard/counts")
      .then((res) => setCounts(res.data))
      .catch((err) => console.error(err));
  }, []);

  // Fetch activities
  useEffect(() => {
    axios
      .get("/api/dashboard/activities")
      .then((res) => {
        const formatted = res.data.map((a) => {
          const dateObj = new Date(a.createdAt || new Date());
          return {
            ...a,
            formattedTime: dateObj.toLocaleString("en-IN", {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
              second: "2-digit",
            }),
          };
        });
        setActivities(formatted);
      })
      .catch((err) => {
        console.error(err);
        setActivities([]);
      });
  }, []);

  // Sync admin data
  useEffect(() => {
    const updateAdminData = () => {
      const data = localStorage.getItem("adminData");
      if (data) setAdmin(JSON.parse(data));
      const storedPerms = JSON.parse(localStorage.getItem("userPermissions") || "[]") || [];
      const fallbackPerms = (JSON.parse(localStorage.getItem("adminData") || "{}")?.permissions) || [];
      setUserPermissions(storedPerms.length ? storedPerms : fallbackPerms);
    };
    updateAdminData();
    window.addEventListener("profileUpdated", updateAdminData);
    window.addEventListener("storage", updateAdminData);

    return () => {
      window.removeEventListener("profileUpdated", updateAdminData);
      window.removeEventListener("storage", updateAdminData);
    };
  }, []);

  // Close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("adminData");
    localStorage.removeItem("token");
    localStorage.removeItem("employeeToken");
    localStorage.removeItem("employeeUser");
    localStorage.removeItem("userPermissions");
    navigate("/");
  };

  // Dashboard cards with PERMISSION KEYS that MATCH AdminManagement permissionsList
  // AdminManagement permissionsList =
  // ["Dashboard_View","Department_View","Designation_View","Leave_Manage","Holiday_Manage",
  //  "Shift_Manage","Policy_Manage","Location_Manage","Payroll_Manage","Employee_View","Admin_Management"]
  const cards = [
    { title: "Master", value: counts.departments, path: "/DepartmentList", color: "bg-green-500 text-white", icon: Building2, permission: "Master_View" },
    { title: "Employees", value: counts.employees, path: "/EmployeeList", color: "bg-blue-500 text-white", icon: Users, permission: "Employee_View" },
    // { title: "Designations", value: counts.designations, path: "/DesignationList", color: "bg-purple-500 text-white", icon: Briefcase, permission: "Designation_View" },
    { title: "Leave Management", value: counts.leaveTypes, path: "/LeaveHistory", color: "bg-pink-500 text-white", icon: Calendar, permission:  "Leave_Management_View", },
    { title: "Shift Management", value: counts.shifts, path: "/ShiftManagement", color: "bg-yellow-500 text-gray-900", icon: Clock, permission: "Shift_Management_view" },
    { title: "Attendance System", value: counts.holidays, path: "/AttendanceController", color: "bg-red-500 text-white", icon: CalendarDays, permission:  "Attendance_history_view" },
    { title: "Payslips", value: counts.policies, path: "/GenerateAllEmployeePayslip", color: "bg-indigo-500 text-white", icon: FileText, permission: "Pay_slip_view" },
    // { title: "Reminder Section", value: counts.locations, path: "/ReminderPage", color: "bg-teal-500 text-white", icon: MapPin, permission: "Reminder_View" },
    { title: "Controller", value: counts.payrollComponents, path: "/AdminManagement", color: "bg-orange-500 text-white", icon: Wallet, permission: "Admin_Management_view", },

    { title: "Activity History", value: counts.locations, path: "/ActivityHistory", color: "bg-teal-500 text-white", icon: History, permission: "Activity_History_view" },
  ];

  // Admin (Admin/mainAdmin/superadmin/root) OR "all" permission -> show all cards
  const normalizedRole = String(admin?.role || "").toLowerCase().replace(/\s+/g, "");
  const isSuper =
    ["mainadmin", "superadmin", "root"].includes(normalizedRole) ||
    hasAllAccess;

  const visibleCards = isSuper
    ? cards
    : cards.filter((c) => userPermissions.includes(c.permission));

  return (
    <div className="flex min-h-screen flex-col md:flex-row">
      <Sidebar />

      <main className="relative flex-1 min-h-screen w-full overflow-hidden">
        <div
          className="absolute inset-0 scale-[1.02] bg-cover bg-center bg-no-repeat blur-[1px]"
          style={{ backgroundImage: `url(${hospitalBg})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/35 via-emerald-900/20 to-white/10" />
        <div className="absolute inset-0 bg-white/18" />

        <div className="relative z-10 p-4">
        <header className="mb-4 flex items-center justify-between gap-3 rounded-2xl border border-white/40 bg-white/65 px-3 py-3 shadow-lg backdrop-blur-md sticky top-4 z-20 sm:px-4">
          <h1 className="text-lg font-semibold sm:text-2xl">Dashboard</h1>

          <div className="relative flex min-w-0 items-center gap-2 sm:gap-3" ref={dropdownRef}>
            <span className="max-w-[120px] truncate text-right text-sm font-medium text-gray-700 sm:max-w-[220px] sm:text-base">
              {admin?.name ?? "Admin"}
            </span>

            <img
              onClick={() => setDropdownOpen(!dropdownOpen)}
              src={eyeLogo}
              alt="Netrapedia logo"
              className="h-9 w-9 shrink-0 rounded-full object-cover cursor-pointer sm:h-10 sm:w-10"
            />

            {dropdownOpen && (
              <div className="absolute right-0 mt-12 w-48 bg-white border rounded shadow-lg z-50">
                <ul className="flex flex-col">
                  {/* <li>
                    <Link to="/EditProfile" className="block px-4 py-2 hover:bg-gray-100" onClick={() => setDropdownOpen(false)}>Edit Profile</Link>
                  </li> */}
                <button 
                  className="w-full text-left block px-4 py-2 hover:bg-gray-100" 
                  onClick={() => { 
                    setIsChangePasswordOpen(true); 
                    setDropdownOpen(false); 
                  }}
                >
                  Change Password
                </button>
                  <li>
                    <button className="w-full text-left px-4 py-2 hover:bg-gray-100" onClick={handleLogout}>Logout</button>
                  </li>
                </ul>
              </div>
            )}
          </div>
        </header>

        {/* <div className="grid grid-cols-1 lg:grid-cols-3 gap-6"> */}
          <div className="lg:col-span-2 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
            {visibleCards.length > 0 ? (
              visibleCards.map((c, i) => {
                const Icon = c.icon;
                return (
                  <Link key={i} to={c.path} className="flex justify-center">
                   <div className={`w-full max-w-sm rounded-2xl border border-white/30 p-6 text-center shadow-lg backdrop-blur-md hover:-translate-y-1 hover:shadow-2xl transition cursor-pointer ${c.color}`}>
                      <div className="flex justify-center mb-3"><Icon size={32} /></div>
                      <h2 className="text-lg font-medium">{c.title}</h2>
                      {/* <p className="text-3xl font-bold mt-2">{c.value ?? 0}</p> */}
                    </div>
                  </Link>
                );
              })
            ) : (
              <p className="text-gray-500">No accessible cards</p>
            )}
          </div>

          {/* Recent Activities */}
          {/* <div className="bg-white p-2 rounded-lg shadow w-full text-sm">
            <h3 className="font-semibold mb-3 flex items-center gap-2 text-sm">
              <Activity size={20} /> Recent Activities
            </h3>
            <ul className="space-y-2 max-h-72 overflow-y-auto">
              {activities.length > 0 ? (
                activities.map((a) => (
                  <li key={a._id} className="p-2 border rounded hover:bg-gray-50">
                    <div>{a.text}</div>
                    <div className="text-xs text-gray-400 mt-1">{a.formattedTime}</div>
                  </li>
                ))
              ) : (
                <p className="text-gray-500">No recent activities</p>
              )}
            </ul>
          </div> */}
        {/* </div> */}
        {isChangePasswordOpen && (
        <div className="fixed inset-0 z-[999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-md">
            <button 
              onClick={() => setIsChangePasswordOpen(false)}
              className="absolute top-4 right-4 text-gray-500 hover:text-red-500 z-10"
            >
              <X size={24} />
            </button>
            <ChangePassword onClose={() => setIsChangePasswordOpen(false)} />
          </div>
        </div>
      )}
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
